import { useRouter } from 'next/router'
import React from 'react'

import clsx from 'clsx'
import { FormattedMessage } from 'react-intl'

import { AnnualForecast } from '@/lib/types'
import { formatCurrency } from '@/lib/utils'

import Rule from '@/components/Rule'

export const ProfitTooltip = ({
  annualForecast,
  currency,
  className
}: {
  annualForecast?: AnnualForecast
  currency: string
  className?: string
}) => {
  const { locale } = useRouter()

  const BTC_PRICES = [
    {
      value: 'current',
      miningProfit:
        annualForecast &&
        formatCurrency(annualForecast?.current.miningProfit, currency, locale),
      devicePrice:
        annualForecast &&
        formatCurrency(annualForecast?.current.devicePrice, currency, locale),
      total:
        annualForecast &&
        formatCurrency(annualForecast?.current.total, currency, locale)
    },
    {
      value: '$75k',
      miningProfit:
        annualForecast &&
        formatCurrency(annualForecast?.['75k'].miningProfit, currency, locale),
      devicePrice:
        annualForecast &&
        formatCurrency(annualForecast?.['75k'].devicePrice, currency, locale),
      total:
        annualForecast &&
        formatCurrency(annualForecast?.['75k'].total, currency, locale),
      percent:
        annualForecast &&
        (annualForecast?.['75k'].devicePrice /
          annualForecast?.current.devicePrice) *
          100
    },
    {
      value: '$100k',
      miningProfit:
        annualForecast &&
        formatCurrency(annualForecast?.['100k'].miningProfit, currency, locale),
      devicePrice:
        annualForecast &&
        formatCurrency(annualForecast?.['100k'].devicePrice, currency, locale),
      total:
        annualForecast &&
        formatCurrency(annualForecast?.['100k'].total, currency, locale),
      percent:
        annualForecast &&
        (annualForecast?.['100k'].devicePrice /
          annualForecast?.current.devicePrice) *
          100
    }
  ]

  return (
    <div className={clsx(className)}>
      <p className="mb-3 hidden text-x-sm font-medium leading-[1.27] tracking-[-0.24px] tablet:block">
        <FormattedMessage
          {...{
            id: 'tooltip.btc.text',
            defaultMessage:
              'Вы зарабатываете BTC, ваша прибыль зависит от того, по какому курсу вы его продаете'
          }}
        />
      </p>
      <div className="grid grid-cols-1 gap-2 tablet:grid-cols-3">
        {BTC_PRICES.map(
          ({ value, miningProfit, devicePrice, percent, total }) => {
            const isCurrentPrice = value === 'current'

            return (
              <div
                className={clsx(
                  'flex flex-auto flex-col justify-between gap-2 rounded-lg px-3 py-2 tablet:justify-center',
                  value === BTC_PRICES[BTC_PRICES.length - 1].value
                    ? 'bg-green-100'
                    : 'border-[0.5px] border-slate-300'
                )}
                key={value}
              >
                <div className="flex justify-between gap-0.5 max-tablet:items-center tablet:flex-col">
                  <span className="text-[11px] leading-[1.27] tracking-[-0.24px] text-secondary-500">
                    <FormattedMessage
                      {...{
                        id: 'tooltip.btc.price',
                        defaultMessage: 'Цена продажи BTC'
                      }}
                    />
                  </span>
                  <span className="items-center text-x-sm font-medium leading-x-base tablet:text-x-base">
                    {isCurrentPrice ? (
                      <FormattedMessage
                        {...{
                          id: 'tooltip.btc.price.current',
                          defaultMessage: 'Текущая'
                        }}
                      />
                    ) : (
                      value
                    )}
                  </span>
                </div>

                <div className="flex justify-between gap-0.5 max-tablet:items-center tablet:flex-col">
                  <span className="text-[11px] leading-[1.27] tracking-[-0.24px] text-secondary-500">
                    <FormattedMessage
                      {...{
                        id: 'tooltip.device.profit.mining',
                        defaultMessage: 'Прибыль от майнинга за год'
                      }}
                    />
                  </span>
                  <span className="text-x-sm font-medium leading-x-sm">
                    ~ {miningProfit || formatCurrency(0, currency, locale)}
                  </span>
                </div>

                <div className="flex justify-between gap-0.5 max-tablet:items-center tablet:flex-col">
                  <span className="text-[11px] leading-[1.27] tracking-[-0.24px] text-secondary-500">
                    <FormattedMessage
                      {...{
                        id: isCurrentPrice
                          ? 'tooltip.device.profit.device'
                          : 'tooltip.device.profit.device.new',
                        defaultMessage: isCurrentPrice
                          ? 'Цена оборудования'
                          : 'Новая цена оборудования'
                      }}
                    />
                  </span>
                  <div className="flex items-center gap-1 text-x-sm font-medium leading-[18px]">
                    <span>
                      ~ {devicePrice || formatCurrency(0, currency, locale)}
                    </span>
                    {percent && (
                      <span className="text-x-[11px] rounded-md border border-slate-300 px-1.5 py-0.5 font-medium leading-3 text-secondary-500">
                        +{percent.toFixed(0)}%
                      </span>
                    )}
                  </div>
                </div>
                <Rule />
                <div className="flex justify-between gap-0.5 max-tablet:items-center tablet:flex-col">
                  <span className="text-[11px] leading-[1.27] tracking-[-0.24px] text-secondary-500">
                    <FormattedMessage
                      {...{
                        id: 'tooltip.device.profit.total',
                        defaultMessage: 'Общая прибыль за год'
                      }}
                    />
                  </span>
                  <span className="text-x-sm font-medium leading-x-sm">
                    ~ {total || formatCurrency(0, currency, locale)}
                  </span>
                </div>
              </div>
            )
          }
        )}
      </div>
    </div>
  )
}
